import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { api } from '../../services/api'
import { useAuthStore } from '../../context/auth'
import { Card, Spinner, StatTile, Badge, Table, EmptyState } from '../../components/ui'
import {
  APPOINTMENT_CHANNEL_LABELS,
  APPOINTMENT_STATUS_COLORS,
  APPOINTMENT_STATUS_LABELS,
  clp,
  formatRut,
  formatTime,
} from '../../utils/format'
import type { Appointment, Payment, Charge } from '../../types/api'

/** Métricas por canal de reserva (mesón vs web) que entrega la API. */
type ChannelMetrics = Record<string, { total: number; byStatus: Record<string, number> }>

function last30Days() {
  const to = new Date()
  to.setDate(to.getDate() + 60) // incluye lo ya reservado hacia adelante
  const from = new Date()
  from.setDate(from.getDate() - 30)
  from.setHours(0, 0, 0, 0)
  return { from: from.toISOString(), to: to.toISOString() }
}

function todayRange() {
  const from = new Date()
  from.setHours(0, 0, 0, 0)
  const to = new Date(from)
  to.setDate(to.getDate() + 1)
  return { from: from.toISOString(), to: to.toISOString() }
}

export function DashboardPage() {
  const user = useAuthStore((s) => s.user)
  const { from, to } = todayRange()
  const canSeeMoney = user && ['ADMIN', 'RECEPTION', 'ACCOUNTANT'].includes(user.role)

  const appointments = useQuery({
    queryKey: ['appointments', 'today'],
    queryFn: async () => (await api.get<Appointment[]>('/appointments', { params: { from, to } })).data,
  })
  const payments = useQuery({
    queryKey: ['payments', 'today'],
    queryFn: async () => (await api.get<Payment[]>('/payments', { params: { from, to } })).data,
    enabled: Boolean(canSeeMoney),
  })
  const pendingCharges = useQuery({
    queryKey: ['charges', 'pending'],
    queryFn: async () => (await api.get<Charge[]>('/charges', { params: { status: 'PENDING' } })).data,
    enabled: Boolean(canSeeMoney),
  })
  const channels = useQuery({
    queryKey: ['appointments', 'metrics', 'channels'],
    queryFn: async () => (await api.get<ChannelMetrics>('/appointments/metrics/channels', { params: last30Days() })).data,
    enabled: Boolean(canSeeMoney),
  })

  if (appointments.isLoading) return <Spinner />

  const appts = appointments.data ?? []
  const active = appts.filter((a) => !['CANCELLED', 'NO_SHOW'].includes(a.status))
  const attended = appts.filter((a) => ['ATTENDED', 'CLOSED'].includes(a.status))
  const webToday = active.filter((a) => a.channel === 'WEB').length
  const todayIncome = (payments.data ?? []).filter((p) => p.status === 'PAID').reduce((s, p) => s + p.amount, 0)
  const pendingTotal = (pendingCharges.data ?? []).reduce((s, c) => s + c.amount, 0)

  return (
    <div className="space-y-5">
      <h1 className="font-display text-2xl font-bold text-ink">
        Hola, {user?.firstName}
      </h1>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <StatTile label="Citas de hoy" value={active.length} accent="ink" />
        <StatTile label="Atendidas" value={attended.length} accent="brand" />
        <StatTile label="Reservadas en línea (hoy)" value={webToday} accent="brand" />
        {canSeeMoney && <StatTile label="Ingresos de hoy" value={clp(todayIncome)} accent="success" />}
        {canSeeMoney && <StatTile label="Por cobrar" value={clp(pendingTotal)} accent="danger" />}
      </div>

      <Card
        title="Agenda de hoy"
        actions={
          <Link to="/app/agenda" className="text-sm font-medium text-brand-700 hover:underline">
            Ver agenda completa →
          </Link>
        }
      >
        {appts.length === 0 ? (
          <EmptyState message="No hay citas agendadas para hoy." />
        ) : (
          <Table headers={['Hora', 'Paciente', 'Prestación', 'Profesional', 'Estado']}>
            {appts.map((a) => (
              <tr key={a.id}>
                <td className="px-2 py-2 font-medium">{formatTime(a.startsAt)}</td>
                <td className="px-2 py-2">
                  {a.patient.firstName} {a.patient.lastName}
                  <span className="block text-xs text-gray-400">{formatRut(a.patient.rut)}</span>
                </td>
                <td className="px-2 py-2">{a.service.name}</td>
                <td className="px-2 py-2">
                  {a.professional.firstName} {a.professional.lastName}
                </td>
                <td className="px-2 py-2">
                  <Badge className={APPOINTMENT_STATUS_COLORS[a.status]}>{APPOINTMENT_STATUS_LABELS[a.status]}</Badge>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      {canSeeMoney && channels.data && (
        <Card title="Canal de reserva — últimos 30 días y lo que viene">
          {/* Captación web vs mesón: cuántas entran por cada vía y cómo terminan. */}
          <Table headers={['Canal', 'Reservas', 'Confirmadas', 'Atendidas', 'Anuladas', 'No asistió']}>
            {(['WEB', 'RECEPTION'] as const).map((channel) => {
              const m = channels.data?.[channel] ?? { total: 0, byStatus: {} }
              const n = (...keys: string[]) => keys.reduce((sum, k) => sum + (m.byStatus[k] ?? 0), 0)
              return (
                <tr key={channel}>
                  <td className="px-2 py-2 font-medium">
                    {channel === 'WEB' ? (
                      <Badge className="bg-brand-100 text-brand-700">{APPOINTMENT_CHANNEL_LABELS.WEB}</Badge>
                    ) : (
                      APPOINTMENT_CHANNEL_LABELS.RECEPTION
                    )}
                  </td>
                  <td className="px-2 py-2 font-semibold">{m.total}</td>
                  <td className="px-2 py-2">{n('CONFIRMED')}</td>
                  <td className="px-2 py-2">{n('ATTENDED', 'CLOSED')}</td>
                  <td className="px-2 py-2">{n('CANCELLED')}</td>
                  <td className="px-2 py-2">{n('NO_SHOW')}</td>
                </tr>
              )
            })}
          </Table>
        </Card>
      )}

      {canSeeMoney && (pendingCharges.data?.length ?? 0) > 0 && (
        <Card
          title="Cobros pendientes"
          actions={
            <Link to="/app/cobros" className="text-sm font-medium text-brand-700 hover:underline">
              Ir a caja →
            </Link>
          }
        >
          <Table headers={['Paciente / Empresa', 'Detalle', 'Monto']}>
            {(pendingCharges.data ?? []).slice(0, 8).map((c) => (
              <tr key={c.id}>
                <td className="px-2 py-2">
                  {c.company ? c.company.businessName : c.patient ? `${c.patient.firstName} ${c.patient.lastName}` : '—'}
                </td>
                <td className="px-2 py-2 text-gray-600">{c.description}</td>
                <td className="px-2 py-2 font-medium">{clp(c.amount)}</td>
              </tr>
            ))}
          </Table>
        </Card>
      )}
    </div>
  )
}
